import React, { useEffect } from 'react';
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';

export interface ToastData {
  id?: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  duration?: number;
}

interface ToastProps {
  toast: ToastData | null;
  onClose: () => void;
}

export function Toast({ toast, onClose }: ToastProps) {
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => {
      onClose();
    }, toast.duration || 4000);

    return () => clearTimeout(timer);
  }, [toast, onClose]);

  if (!toast) return null;

  const config = {
    success: {
      icon: <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />,
      border: 'border-emerald-500/30',
      badge: 'bg-emerald-500/10 text-emerald-400',
    },
    error: {
      icon: <AlertCircle className="w-5 h-5 text-rose-400 shrink-0" />,
      border: 'border-rose-500/30',
      badge: 'bg-rose-500/10 text-rose-400',
    },
    warning: {
      icon: <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />,
      border: 'border-amber-500/30',
      badge: 'bg-amber-500/10 text-amber-400',
    },
    info: {
      icon: <Info className="w-5 h-5 text-blue-400 shrink-0" />,
      border: 'border-blue-500/30',
      badge: 'bg-blue-500/10 text-blue-400',
    },
  }[toast.type || 'info'];

  return (
    <div
      id="toast-notification"
      className={`fixed bottom-6 right-6 z-[9999] flex max-w-md items-start gap-3.5 rounded-xl bg-slate-900 px-4 py-3.5 text-white shadow-2xl border ${config.border} animate-in fade-in slide-in-from-bottom-5 duration-300 backdrop-blur-md`}
    >
      {config.icon}
      <div className="flex-1 text-left min-w-0 pr-1">
        {toast.title && (
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-200 mb-0.5">
            {toast.title}
          </h4>
        )}
        <p className="text-sm font-medium text-slate-100 leading-snug break-words">
          {toast.message}
        </p>
      </div>
      <button
        id="btn-close-toast"
        onClick={onClose}
        className="text-slate-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-slate-800 -mr-1 -mt-1 cursor-pointer shrink-0"
        title="Fechar notificação"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
